import { Component, HostListener, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { TicketService } from '../../../core/services/ticket.service';
import { Ticket } from '../../../core/models/ticket.model';
import { StatusBadgeComponent } from '../status-badge/status-badge.component';
import { PriorityBadgeComponent } from '../priority-badge/priority-badge.component';

@Component({
  selector: 'app-global-search-modal',
  standalone: true,
  imports: [CommonModule, FormsModule, StatusBadgeComponent, PriorityBadgeComponent],
  template: `
    @if (isOpen()) {
      <div (click)="closeModal()" class="fixed inset-0 z-50 flex items-start justify-center pt-20 p-4 bg-slate-950/70 backdrop-blur-xs animate-in fade-in">
        <div (click)="$event.stopPropagation()" class="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl max-w-2xl w-full overflow-hidden shadow-2xl space-y-0">
          
          <!-- Search Header Input -->
          <div class="px-4 py-3.5 border-b border-slate-200 dark:border-slate-800 flex items-center space-x-3 bg-slate-50 dark:bg-slate-900/50">
            <svg class="w-5 h-5 text-slate-400 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
            </svg>
            <input
              #searchInput
              [(ngModel)]="searchQuery"
              (ngModelChange)="onSearchChange()"
              type="text"
              placeholder="Search by ticket number, title, or description... (Ctrl + K)"
              class="w-full bg-transparent text-sm text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-slate-500 focus:outline-hidden" />
            <kbd class="px-2 py-0.5 text-[10px] font-mono font-bold text-slate-500 dark:text-slate-400 bg-slate-100 dark:bg-slate-800 rounded-md border border-slate-200 dark:border-slate-700">ESC</kbd>
          </div>

          <!-- Results List -->
          <div class="max-h-96 overflow-y-auto divide-y divide-slate-100 dark:divide-slate-800/60 p-2">
            @if (results().length === 0 && searchQuery.trim()) {
              <div class="p-8 text-center text-slate-500 dark:text-slate-400 text-xs">
                No tickets matching "<span class="text-slate-900 dark:text-white font-medium">{{ searchQuery }}</span>"
              </div>
            } @else if (results().length === 0) {
              <div class="p-8 text-center text-slate-400 dark:text-slate-500 text-xs">
                Type to instantly search across tickets, departments, and activities.
              </div>
            } @else {
              @for (ticket of results(); track ticket.id) {
                <div
                  (click)="selectResult(ticket)"
                  class="p-3 rounded-xl hover:bg-slate-100 dark:hover:bg-slate-800/70 transition-colors cursor-pointer flex items-center justify-between group">
                  <div class="space-y-1 min-w-0 pr-3">
                    <div class="flex items-center space-x-2">
                      <span class="text-xs font-mono font-bold text-blue-600 dark:text-blue-400 group-hover:underline">{{ ticket.ticketNumber }}</span>
                      <span class="text-xs font-medium text-slate-800 dark:text-slate-200 truncate">{{ ticket.title }}</span>
                    </div>
                    <p class="text-[11px] text-slate-500 dark:text-slate-400 line-clamp-1">{{ ticket.description }}</p>
                  </div>
                  <div class="flex items-center space-x-2 shrink-0">
                    <app-priority-badge [priority]="ticket.priority" />
                    <app-status-badge [status]="ticket.status" />
                  </div>
                </div>
              }
            }
          </div>

          <!-- Modal Footer -->
          <div class="px-4 py-2.5 bg-slate-50 dark:bg-slate-950/60 border-t border-slate-200 dark:border-slate-800 text-[11px] text-slate-500 flex items-center justify-between">
            <span>Press <kbd class="px-1.5 py-0.5 text-[9px] font-mono bg-slate-200 dark:bg-slate-800 rounded-md text-slate-700 dark:text-slate-300">Enter</kbd> to view details</span>
            <span>Savinda Internal Ticketing</span>
          </div>

        </div>
      </div>
    }
  `,
})
export class GlobalSearchModalComponent {
  private readonly ticketService = inject(TicketService);
  private readonly router = inject(Router);

  readonly isOpen = signal(false);
  searchQuery = '';
  readonly results = signal<Ticket[]>([]);

  @HostListener('window:keydown', ['$event'])
  handleKeyboardEvent(event: KeyboardEvent): void {
    if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'k') {
      event.preventDefault();
      this.toggleModal();
    } else if (event.key === 'Escape' && this.isOpen()) {
      this.closeModal();
    }
  }

  toggleModal(): void {
    if (this.isOpen()) {
      this.closeModal();
    } else {
      this.openModal();
    }
  }

  openModal(): void {
    this.isOpen.set(true);
    this.searchQuery = '';
    this.results.set([]);
  }

  closeModal(): void {
    this.isOpen.set(false);
  }

  onSearchChange(): void {
    if (!this.searchQuery.trim()) {
      this.results.set([]);
      return;
    }
    const q = this.searchQuery.toLowerCase();
    const matches = this.ticketService.tickets().filter(
      (t) =>
        t.ticketNumber.toLowerCase().includes(q) ||
        t.title.toLowerCase().includes(q) ||
        t.description.toLowerCase().includes(q)
    );
    this.results.set(matches);
  }

  selectResult(ticket: Ticket): void {
    this.ticketService.selectTicket(ticket);
    this.closeModal();
    this.router.navigate(['/tickets']);
  }
}
